import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import useCurrentSnap from '../../core/hooks/useCurrentSnap';
import { useUtils } from '../../core/hooks/useUtils';
import { getAuthLinkAction, socialAction } from '../../store/Auth/action';
import { addCommentAction } from '../../store/Comments/action';
import { getSharedSnapAction } from '../../store/SharedSnap/action';
import { setLastRoute } from '../../core/helpers/lastRoute';

interface IOutput {
	state: HookState;
	currentChart: any;
	sharedSnap: any;
	user: any;
	sendMessage(): void;
	toggleModal(): void;
	salesforceLogin(): void;
	handleLogin(googleData: any): void;
	handleChange(e: React.ChangeEvent<HTMLInputElement>): void;
}

interface HookState {
	modalIsOpen: boolean;
	isGoogleLoading: boolean;
	isSalesLoading: boolean;
	comment: string;
}

export default function useHook(): IOutput {
	const { token } = useParams<{ token: string }>();
	const {
		dispatch,
		store: { sharedSnap, user },
	} = useUtils();
	const { snap } = sharedSnap.data;

	const [state, setState] = useState<HookState>({
		modalIsOpen: false,
		isGoogleLoading: false,
		isSalesLoading: false,
		comment: '',
	});

	useEffect(() => {
		dispatch(getSharedSnapAction(token));
		// eslint-disable-next-line
	}, [token]);

	const { currentChart } = useCurrentSnap({ item: snap });

	const sendMessage = () => {
		if (!comment.trim()) return;
		if (!user.data._id) {
			toggleModal();
		} else {
			addComment(user.data);
		}
	};

	const addComment = (user?: any): void => {
		const { firstName, lastName, _id } = user;
		const fullName = `${firstName || ''} ${lastName || ''}`;
		dispatch(
			addCommentAction({
				data: {
					name: fullName,
					content: comment,
					sharedSnapId: snap._id,
					userId: _id,
				},
			})
		);
		setState(prev => ({
			...prev,
			comment: '',
		}));
	};

	const toggleModal = () => {
		setState(prev => ({ ...prev, modalIsOpen: !prev.modalIsOpen }));
	};

	const salesforceLogin = () => {
		toggleLoader('isSalesLoading');
		setLastRoute(`/snap/shared/${token}`);
		const data = {
			content: comment,
			sharedSnapId: snap._id,
		};
		!!comment.trim() && localStorage.setItem('comment', JSON.stringify(data));
		const windowReference = window.open();
		dispatch(
			getAuthLinkAction({
				callBack: ({ link }: any) => {
					toggleLoader('isSalesLoading');
					windowReference!.location = link;
				},
			})
		);
	};

	const handleLogin = (googleData: any) => {
		toggleLoader('isGoogleLoading');
		const { tokenId } = googleData;
		dispatch(
			socialAction({
				token: tokenId,
				callBack: (user: any) => {
					toggleLoader('isGoogleLoading');
					toggleModal();
					!!comment.trim() && addComment(user);
				},
				withoutRedirect: true,
			})
		);
	};

	const toggleLoader = (field: keyof typeof state): void => {
		setState(prev => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		setState(prev => ({
			...prev,
			comment: e.target.value,
		}));
	};

	const { comment } = state;

	return {
		state,
		currentChart,
		sharedSnap,
		user: user.data,
		sendMessage,
		toggleModal,
		salesforceLogin,
		handleLogin,
		handleChange,
	};
}
